import { create } from "zustand";
import {
  persist,
  createJSONStorage,
  type StateStorage,
} from "zustand/middleware";
import type { ProductVariantDto } from "../../api";
import { LOCAL_STORAGE_KEYS } from "../../utils/localStorageKeys";
import { getProductLineTotal } from "../../modules/products/utils/get-pricelist-price";

/**
 * Per-line warranty selection. The buyer picks one warranty SKU from the
 * product's `compatible_warranty_ids` via the checkout warranty modal.
 * Persisted in the cart so the choice survives navigation; included in the
 * quotation payload so the BE emits a linked warranty sale.order.line.
 */
export interface CartLineWarranty {
  /** product.product id of the warranty SKU. */
  productId: number;
  name: string;
  price: number;
}

export interface CartItem {
  product: ProductVariantDto;
  quantity: number;
  warranty?: CartLineWarranty | null;
}

interface CartState {
  items: CartItem[];
  addToCart: (product: ProductVariantDto, quantity: number) => void;
  removeFromCart: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  setLineWarranty: (productId: number, warranty: CartLineWarranty | null) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

// Get the current user ID from localStorage
function getCurrentUserId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER_ID);
}

// Get the user-specific storage key
function getCartStorageKey(): string {
  const userId = getCurrentUserId();
  return userId ? `cart-storage-${userId}` : "cart-storage-guest";
}

// Custom storage that uses user-specific keys
// Note: We ignore the 'name' parameter from Zustand and use our own dynamic key
const userSpecificStorage: StateStorage = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getItem: (_name: string): string | null => {
    const key = getCartStorageKey();
    return localStorage.getItem(key);
  },
  setItem: (_name: string, value: string): void => {
    const key = getCartStorageKey();
    localStorage.setItem(key, value);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeItem: (_name: string): void => {
    const key = getCartStorageKey();
    localStorage.removeItem(key);
  },
};

// Cap a desired qty against the variant's supplier_stock. Treat null /
// undefined / non-positive values as "no cap" — Odoo hasn't populated stock
// for that SKU yet, so the BE is the final gate.
function clampToStock(product: ProductVariantDto, desired: number): number {
  const stock = product.supplierStock;
  if (typeof stock !== "number" || stock <= 0) return Math.max(0, desired);
  return Math.max(0, Math.min(desired, stock));
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      addToCart: (product, quantity) => {
        set((state) => {
          const existingItem = state.items.find(
            (item) => item.product.id === product.id
          );
          if (existingItem) {
            return {
              items: state.items.map((item) =>
                item.product.id === product.id
                  ? {
                      ...item,
                      quantity: clampToStock(
                        item.product,
                        item.quantity + quantity,
                      ),
                    }
                  : item
              ),
            };
          }
          return {
            items: [
              ...state.items,
              { product, quantity: clampToStock(product, quantity) },
            ],
          };
        });
      },
      removeFromCart: (productId) => {
        set((state) => ({
          items: state.items.filter((item) => item.product.id !== productId),
        }));
      },
      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(productId);
          return;
        }
        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId
              ? { ...item, quantity: clampToStock(item.product, quantity) }
              : item
          ),
        }));
      },
      setLineWarranty: (productId, warranty) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId ? { ...item, warranty } : item,
          ),
        }));
      },
      clearCart: () => {
        set({ items: [] });
      },
      getTotalItems: () => {
        return get().items.reduce((total, item) => total + item.quantity, 0);
      },
      getTotalPrice: () => {
        return get().items.reduce((total, item) => {
          const productTotal = getProductLineTotal(item.product, item.quantity);
          const warrantyTotal = item.warranty
            ? item.warranty.price * item.quantity
            : 0;
          return total + productTotal + warrantyTotal;
        }, 0);
      },
    }),
    {
      name: "cart-storage",
      storage: createJSONStorage(() => userSpecificStorage),
      // Skip initial hydration - we'll handle it manually in useUserCartSync
      skipHydration: true,
    }
  )
);
